import { useCanAccessUsersResource } from '../../auth/useCanAccessUsersResource';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { ReferenceField } from '../../components/admin/reference-field';
import { TextField } from '../../components/admin/text-field';
import { Badge } from '../../components/ui/badge';
import { saveSpreadsheet } from '../../utils/spreadsheet/save';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListFilterActions } from '../Moonstar/MoonstarListFilterActions';
import { ResourcesValue } from '../resources';
import { UserDisplayNameField } from '../User/UserDisplayNameField';
import { PostFilters } from './PostFilters';
import { PostToolbarActions } from './PostToolbarActions';
import { RichContent } from './RichContent';

const CONTENT_V1_TAGGED_USER_REGEX = /\{@\}\[(.*?)\]\(.*?\)/g;

const exporter = async (posts: any) => {
  function richContentToText(richContent: string | undefined) {
    if (!richContent) return richContent;

    return richContent.replaceAll(CONTENT_V1_TAGGED_USER_REGEX, '@$1');
  }

  const mapped = posts.map((p: any) => {
    return {
      Id: p.id,
      Topic: p.topic,
      CreatedOn: p.createdOn,
      AuthorId: p.createdById,
      AuthorInternalID: p.author.internalId,
      Author: p.author.name,
      Department: p.author.department,
      JobTitle: p.author.jobTitle,
      Region: p.author.region,
      Units: p.author.units,
      Title: p.title,
      Content: richContentToText(p.richContent?.v1?.content),
      Views: p.numberOfViews,
      Comments: p.numberOfComments,
      Likes: p.numberOfReactions,
      Badge: p.badge,
      AwardedUser: p.awardedUser,
      AwardedUserId: p.awardedUserId,
      AwardedUserInternalID: p.awardedUserInternalId,
    };
  });

  await saveSpreadsheet({
    format: 'csv',
    data: mapped,
    basename: ResourcesValue.POSTS,
  });
};

export const PostList = () => {
  const canAccessUsers = useCanAccessUsersResource();
  const userLink = canAccessUsers ? 'edit' : false;

  return (
    <MoonstarList
      sort={{ field: 'createdOn', order: 'DESC' }}
      actions={<PostToolbarActions />}
      filters={PostFilters}
      filterActions={<MoonstarListFilterActions />}
      exporter={exporter}
    >
      <DataTable
        bulkActionButtons={false}
        // No edit or show page for posts
        rowClick={false}
        hiddenColumns={['badge', 'awardedUserId']}
      >
        <DataTable.Col
          source="id"
          cellClassName="text-muted-foreground text-xs tabular-nums"
        />
        <DataTable.Col
          source="createdOn"
          cellClassName="text-muted-foreground text-xs"
        >
          <DateField
            source="createdOn"
            showTime
            options={{
              dateStyle: 'medium',
              timeStyle: 'short',
            }}
          />
        </DataTable.Col>
        <DataTable.Col source="createdById">
          <ReferenceField
            source="createdById"
            reference="users"
            link={userLink}
          >
            <UserDisplayNameField />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="topicId" className="min-w-48">
          <ReferenceField source="topicId" reference="topics" link={false}>
            <Badge
              variant="secondary"
              className="h-auto max-w-48 rounded-md border-primary/10 bg-accent px-2.5 py-1 text-[0.8125rem]/5 break-words whitespace-normal text-accent-foreground"
            >
              <TextField source="displayName" />
            </Badge>
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col
          source="postContent"
          className="w-full min-w-64 max-w-0"
          render={(record) => (
            <div className="truncate">
              <PostContent record={record as any} />
            </div>
          )}
        />
        <DataTable.NumberCol
          source="numberOfViews"
          disableSort
          cellClassName="tabular-nums"
          conditionalClassName={(record) =>
            record.numberOfViews > 0 ? 'font-medium' : 'text-muted-foreground'
          }
        />
        <DataTable.NumberCol
          source="numberOfComments"
          disableSort
          cellClassName="tabular-nums"
          conditionalClassName={(record) =>
            record.numberOfComments > 0
              ? 'font-medium'
              : 'text-muted-foreground'
          }
        />
        <DataTable.NumberCol
          source="numberOfReactions"
          disableSort
          cellClassName="tabular-nums"
          conditionalClassName={(record) =>
            record.numberOfReactions > 0
              ? 'font-medium'
              : 'text-muted-foreground'
          }
        />
        <DataTable.Col source="badge" disableSort />
        <DataTable.Col source="awardedUserId">
          <ReferenceField
            source="awardedUserId"
            reference="users"
            link={userLink}
          >
            <UserDisplayNameField />
          </ReferenceField>
        </DataTable.Col>
      </DataTable>
    </MoonstarList>
  );
};

function PostContent(props: {
  record: { title?: string; richContent: { v1: { content?: string } } };
}) {
  const { title, richContent } = props.record;

  if (title) {
    if (richContent.v1.content) {
      return (
        <>
          <span className="font-medium">{title}</span>:{' '}
          <RichContent record={props.record} />
        </>
      );
    } else {
      return <span className="font-medium">{title}</span>;
    }
  } else {
    return <RichContent record={props.record} />;
  }
}
